import axios from 'axios';
import { ethers } from 'ethers';

// 根据环境变量或默认值设置 API 基础 URL
// 优先使用 /api（通过 Vercel rewrites 代理），这样不需要每次更新后端 URL
// 如果设置了 VITE_API_URL 且不是开发环境，则使用完整 URL
const getApiBase = () => {
  // 生产环境优先使用 /api（通过 vercel.json rewrites）
  if (import.meta.env.PROD) {
    return '/api';
  }
  
  // 开发环境或设置了 VITE_API_URL
  if (import.meta.env.VITE_API_URL) {
    const url = import.meta.env.VITE_API_URL.endsWith('/') 
      ? import.meta.env.VITE_API_URL.slice(0, -1) 
      : import.meta.env.VITE_API_URL;
    return url + '/api';
  }
  
  // 默认使用 /api
  return '/api';
};

const API_BASE = getApiBase();

// API 客户端
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 服务信息接口
export interface ServiceInfo {
  name: string;
  description: string;
  version: string;
  network: {
    name: string;
    chainId: number;
    rpcUrl: string;
  };
  payment: {
    protocol: string;
    price: string;
    receiver: string;
  };
}

// 支付要求接口
export interface PaymentRequirement {
  scheme: string;
  network: string;
  chainId: number;
  maxAmountRequired: string;
  payTo: string;
  asset: string;
  validUntil: number;
  resource: string;
  description?: string;
}

// 预测结果接口
export interface PredictionResult {
  queryId: string;
  symbol: string;
  interval: string;
  predictions: PredictionData[];
  confidence: number;
  generatedAt: number;
  modelInfo: {
    name: string;
    version: string;
  };
}

export interface PredictionData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  trend: 'up' | 'down' | 'neutral';
  changePercent: number;
}

// 获取服务信息
export async function getServiceInfo(): Promise<ServiceInfo> {
  const response = await api.get('/info');
  return response.data;
}

// 获取预测服务信息
export async function getPredictionInfo() {
  const response = await api.get('/prediction/info');
  return response.data;
}

// 获取支付价格和要求
export async function getPaymentRequirements(resource: string = 'prediction'): Promise<{
  price: string;
  priceWei: string;
  paymentRequirements: {
    x402Version: string;
    accepts: PaymentRequirement[];
  };
}> {
  const response = await api.get('/prediction/price', { params: { resource } });
  return response.data;
}

// 获取用户 nonce
export async function getNonce(address: string): Promise<number> {
  const response = await api.get(`/prediction/nonce/${address}`);
  return response.data.nonce;
}

// 获取用户余额
export async function getBalance(address: string): Promise<string> {
  const response = await api.get(`/prediction/balance/${address}`);
  return response.data.balance;
}

// 获取演示预测
export async function getDemoPrediction(symbol: string, interval: string): Promise<{
  success: boolean;
  isDemo: boolean;
  note: string;
  prediction: PredictionResult;
}> {
  const response = await api.get('/prediction/demo', {
    params: { symbol, interval },
  });
  return response.data;
}

// 获取历史数据
export async function getHistoricalData(symbol: string, interval: string, limit: number = 100) {
  const response = await api.get(`/prediction/history/${symbol}`, {
    params: { interval, limit },
  });
  return response.data;
}

// 构造支付负载
export function createPaymentPayload(
  signer: ethers.JsonRpcSigner,
  requirement: PaymentRequirement,
  nonce: number
): Promise<string> {
  return new Promise(async (resolve, reject) => {
    try {
      const payload = {
        x402Version: '1.0.0',
        scheme: requirement.scheme,
        chainId: requirement.chainId,
        amount: requirement.maxAmountRequired,
        payTo: requirement.payTo,
        payer: await signer.getAddress(),
        asset: requirement.asset,
        validUntil: requirement.validUntil,
        nonce,
        resource: requirement.resource,
        signature: '',
      };

      // 构造签名消息
      const messageHash = ethers.keccak256(
        ethers.solidityPacked(
          ['address', 'uint256', 'uint256', 'address', 'uint256', 'address', 'uint256'],
          [
            payload.payer,
            payload.amount,
            payload.validUntil,
            payload.payTo,
            payload.nonce,
            ethers.ZeroAddress, // 合约地址，这里用零地址
            payload.chainId,
          ]
        )
      );

      // 签名
      const signature = await signer.signMessage(ethers.getBytes(messageHash));
      payload.signature = signature;

      // Base64 编码
      const encodedPayload = btoa(JSON.stringify(payload));
      resolve(encodedPayload);
    } catch (error) {
      reject(error);
    }
  });
}

// 执行付费预测查询
export async function getPaidPrediction(
  paymentHeader: string,
  symbol: string,
  interval: string,
  predictionLength: number
): Promise<{
  success: boolean;
  payment: {
    payer: string;
    amount: string;
    queryHash: string;
  };
  prediction: PredictionResult;
}> {
  const response = await api.post('/prediction', {
    symbol,
    interval,
    predictionLength,
  }, {
    headers: {
      'X-PAYMENT': paymentHeader,
    },
  });
  return response.data;
}

export default api;

